import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import QuickStats from '../components/QuickStats';
import PortfolioOverview from '../components/PortfolioOverview';
import RecentActivity from '../components/RecentActivity';
import { useAuth } from '../contexts/AuthContext';
import { accountsApi, tradingApi, dividendApi, Position, Order, TradingAccount } from '../services/api';

const Dashboard: React.FC = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [accountId, setAccountId] = useState<number | null>(null);
  const [availableAccounts, setAvailableAccounts] = useState<TradingAccount[]>([]);

  const [stats, setStats] = useState({
    totalValue: 0,
    cashBalance: 0,
    totalPositions: 0,
    openOrders: 0,
    upcomingDividends: 0
  });

  const [account, setAccount] = useState({
    equity: 0,
    cash: 0,
    buyingPower: 0,
    portfolioValue: 0
  });

  const [positions, setPositions] = useState<any[]>([]);
  const [activities, setActivities] = useState<any[]>([]);

  // Load available accounts on mount
  useEffect(() => {
    const loadAccounts = async () => {
      if (!token) return;

      try {
        const accounts = await accountsApi.listAccounts(token);
        setAvailableAccounts(accounts);

        if (accounts.length === 0) {
          setError('No trading account found. Please add an account first.');
          setLoading(false);
          return;
        }

        // Set initial account (default or first)
        const defaultAccount = accounts.find(acc => acc.is_default) || accounts[0];
        setAccountId(defaultAccount.id);
      } catch (err: any) {
        console.error('Failed to load accounts:', err);
        setError('Failed to load accounts');
        setLoading(false);
      }
    };

    loadAccounts();
  }, [token]);

  // Fetch dashboard data when account changes
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!token || !accountId) return;

      setLoading(true);
      setError(null);

      try {

        // Fetch account info, positions, and orders in parallel
        const [accountInfo, positionsData, openOrders, closedOrders, upcomingDividends] = await Promise.all([
          tradingApi.getAccountInfo(accountId, token),
          tradingApi.getPositions(accountId, token),
          tradingApi.getOrders(accountId, token, 'open'),
          tradingApi.getOrders(accountId, token, 'closed').catch(() => [] as Order[]),
          dividendApi.getDividends(1, true).catch(() => [])
        ]);

        // Update account info
        setAccount({
          equity: accountInfo.equity,
          cash: accountInfo.cash,
          buyingPower: accountInfo.buying_power,
          portfolioValue: accountInfo.portfolio_value
        });

        // Update stats
        setStats({
          totalValue: accountInfo.portfolio_value,
          cashBalance: accountInfo.cash,
          totalPositions: positionsData.length,
          openOrders: openOrders.length,
          upcomingDividends: upcomingDividends.length
        });

        // 6. Transform positions data
        const transformedPositions = positionsData.map((pos: Position) => ({
          symbol: pos.symbol,
          quantity: pos.quantity,
          entryPrice: pos.avg_entry_price,
          currentPrice: pos.current_price,
          marketValue: pos.market_value,
          unrealizedPnL: pos.unrealized_pl,
          unrealizedPnLPercent: pos.unrealized_plpc
        }));
        setPositions(transformedPositions);

        // 7. Transform orders data (combine open and recent closed orders)
        const allOrders = [...openOrders, ...closedOrders.slice(0, 10)];
        const transformedActivities = allOrders.map((order: Order) => ({
          id: order.id,
          symbol: order.symbol,
          side: order.side.toLowerCase() as 'buy' | 'sell',
          quantity: order.quantity,
          price: order.filled_avg_price || order.limit_price || 0,
          status: order.status.toLowerCase() as 'filled' | 'pending' | 'cancelled' | 'rejected',
          timestamp: order.filled_at || order.submitted_at || order.created_at || new Date().toISOString(),
          type: order.order_type.toLowerCase() as 'market' | 'limit'
        }));
        setActivities(transformedActivities);

      } catch (err: any) {
        console.error('Failed to fetch dashboard data:', err);
        setError(err.message || 'Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [token, accountId]);

  // Loading state
  if (loading) {
    return (
      <Layout>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px'
        }}>
          <div style={{
            fontSize: '18px',
            color: '#6b7280'
          }}>
            Loading dashboard...
          </div>
        </div>
      </Layout>
    );
  }

  // Error state
  if (error) {
    return (
      <Layout>
        <div style={{
          backgroundColor: 'white',
          padding: '24px',
          borderRadius: '8px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px'
          }}>
            ⚠️
          </div>
          <h2 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#ef4444',
            marginBottom: '12px'
          }}>
            {error}
          </h2>
          <p style={{
            color: '#6b7280',
            marginBottom: '24px'
          }}>
            {error.includes('No trading account')
              ? 'Please navigate to the Accounts page to add your Alpaca trading account.'
              : 'Please check your account credentials and try again.'}
          </p>
          <button
            onClick={() => window.location.reload()}
            style={{
              padding: '10px 20px',
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '24px'
      }}>
        {/* Account Selector */}
        {availableAccounts.length > 0 && (
          <div style={{
            backgroundColor: 'white',
            padding: '16px 24px',
            borderRadius: '8px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <label style={{
              fontSize: '14px',
              fontWeight: '600',
              color: '#374151'
            }}>
              Viewing Account:
            </label>
            <select
              value={accountId || ''}
              onChange={(e) => setAccountId(Number(e.target.value))}
              style={{
                padding: '8px 12px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                color: '#1e293b',
                backgroundColor: 'white',
                cursor: 'pointer',
                minWidth: '250px'
              }}
            >
              {availableAccounts.map((acc) => (
                <option key={acc.id} value={acc.id}>
                  {acc.name} ({acc.account_type}) {acc.is_default ? '⭐' : ''}
                </option>
              ))}
            </select>
            <span style={{
              fontSize: '12px',
              color: '#6b7280',
              marginLeft: 'auto'
            }}>
              {availableAccounts.length} account{availableAccounts.length !== 1 ? 's' : ''} available
            </span>
          </div>
        )}

        {/* Quick Stats */}
        <QuickStats stats={stats} />

        {/* Portfolio Overview and Recent Activity */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(600px, 1fr))',
          gap: '24px'
        }}>
          <PortfolioOverview account={account} positions={positions} />
          <RecentActivity activities={activities} maxItems={6} />
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
